// Refine DataProvider routing all CRUD through the LebinaTikra Filter API
// (replaces @refinedev/supabase). See Story 3.7b for the resource→path map
// and acceptance criteria. Auth, RLS, audit, rate-limit are all enforced
// by the Filter API middleware stack — the browser never talks to Supabase
// REST directly anymore.
import type {
  BaseRecord,
  CrudFilters,
  CrudSorting,
  DataProvider,
  HttpError,
  MetaQuery,
  Pagination,
} from '@refinedev/core';
import {
  FilterApiError,
  filterApiFetch,
  filterApiFetchEnvelope,
} from '../lib/filter-api-client';

type Resource = 'communities' | 'organizations' | 'users' | 'groups' | 'invitations' | 'audit-log' | 'moderation-queue' | 'moderation-decisions';

interface PathBuilder {
  list: (meta?: MetaQuery) => string;
  one: (id: string | number, meta?: MetaQuery) => string;
  create?: (meta?: MetaQuery) => string;
  update?: (id: string | number, meta?: MetaQuery) => string;
  delete?: (id: string | number, meta?: MetaQuery) => string;
}

function requireParentId(meta: MetaQuery | undefined, resource: Resource): string {
  const parentId = meta?.parentId as string | undefined;
  if (!parentId) {
    throw new Error(
      `Missing meta.parentId for resource '${resource}' — pass via useTable/useForm/useShow meta.`,
    );
  }
  return parentId;
}

const PATHS: Record<Resource, PathBuilder> = {
  communities: {
    list: () => '/v1/communities',
    one: (id) => `/v1/communities/${id}`,
    create: () => '/v1/communities',
    // update: TODO Story 3.x (community edit PATCH).
    delete: (id) => `/v1/communities/${id}`,
  },
  organizations: {
    list: (m) => `/v1/communities/${requireParentId(m, 'organizations')}/organizations`,
    one: (id) => `/v1/organizations/${id}`,
    create: (m) => `/v1/communities/${requireParentId(m, 'organizations')}/organizations`,
    // update: TODO Story 3.x (org edit PATCH).
    delete: (id) => `/v1/organizations/${id}`,
  },
  users: {
    list: () => '/v1/users',
    one: (id) => `/v1/users/${id}`,
    update: (id) => `/v1/users/${id}`,
    // create: invite flow — POST /v1/organizations/:id/invitations (see invitations-api.ts).
  },
  groups: {
    list: (m) => `/v1/organizations/${requireParentId(m, 'groups')}/groups`,
    one: (id, m) => `/v1/organizations/${requireParentId(m, 'groups')}/groups/${id}`,
    create: (m) => `/v1/organizations/${requireParentId(m, 'groups')}/groups`,
    update: (id, m) => `/v1/organizations/${requireParentId(m, 'groups')}/groups/${id}`,
  },
  invitations: {
    list: (m) => `/v1/organizations/${requireParentId(m, 'invitations')}/invitations`,
    one: (id, m) =>
      `/v1/organizations/${requireParentId(m, 'invitations')}/invitations/${id}`,
    create: (m) => `/v1/organizations/${requireParentId(m, 'invitations')}/invitations`,
  },
  'audit-log': {
    list: (m) => {
      const scope = (m?.scope as 'community' | 'organization' | undefined) ?? 'community';
      const parentId = requireParentId(m, 'audit-log');
      return scope === 'community'
        ? `/v1/communities/${parentId}/audit-log`
        : `/v1/organizations/${parentId}/audit-log`;
    },
    one: (id) => `/v1/audit-log/${id}`,
  },
  'moderation-queue': {
    list: (m) => {
      const scope = (m?.scope as 'community' | 'organization' | undefined) ?? 'community';
      const parentId = requireParentId(m, 'moderation-queue');
      return scope === 'community'
        ? `/v1/communities/${parentId}/moderation-queue`
        : `/v1/organizations/${parentId}/moderation-queue`;
    },
    one: () => { throw new Error('moderation-queue has no detail view'); },
  },
  'moderation-decisions': {
    list: () => { throw new Error('Use moderation-queue for listing'); },
    one: (id) => `/v1/moderation-decisions/${id}`,
    create: () => '/v1/moderation-decisions',
  },
};

function buildQuery(
  pagination?: Pagination,
  filters?: CrudFilters,
  sorters?: CrudSorting,
): string {
  const sp = new URLSearchParams();
  if (pagination?.mode !== 'off') {
    if (pagination?.currentPage != null) sp.set('page', String(pagination.currentPage));
    if (pagination?.pageSize != null) sp.set('page_size', String(pagination.pageSize));
  }
  // Filter API supports single sort field; take only the first.
  const firstSort = sorters?.[0];
  if (firstSort) sp.set('sort', `${firstSort.field}:${firstSort.order}`);
  filters?.forEach((f) => {
    // Skip logical (and/or) filters silently — Filter API doesn't model them.
    if (!('field' in f)) return;
    if (f.operator !== 'eq' && f.operator !== 'contains') {
      console.warn(
        `[filter-api dp] filter operator '${f.operator}' not supported by Filter API, skipped`,
      );
      return;
    }
    sp.set(`filter[${f.field}]`, String(f.value));
  });
  const s = sp.toString();
  return s ? `?${s}` : '';
}

function toHttpError(err: unknown): HttpError {
  if (err instanceof FilterApiError) {
    return {
      statusCode: err.status,
      message: err.message,
      errors: { code: err.code, message_en: err.messageEn },
    };
  }
  if (err instanceof Error) return { statusCode: 0, message: err.message };
  return { statusCode: 0, message: 'unknown' };
}

function methodNotAllowed(resource: string, action: string): HttpError {
  return toHttpError(
    new FilterApiError({
      code: 'METHOD_NOT_ALLOWED',
      message: `${action} not allowed for ${resource}`,
      messageEn: `${action} not allowed for ${resource}`,
      status: 405,
    }),
  );
}

function resolvePath(resource: string): PathBuilder {
  const p = PATHS[resource as Resource];
  if (!p) throw new Error(`Unknown resource for filter-api data provider: ${resource}`);
  return p;
}

export function createFilterApiDataProvider(): DataProvider {
  return {
    getApiUrl: () => (import.meta.env.VITE_FILTER_API_URL as string | undefined) ?? '',

    async getList<TData extends BaseRecord = BaseRecord>({
      resource,
      pagination,
      filters,
      sorters,
      meta,
    }: Parameters<DataProvider['getList']>[0]) {
      const p = resolvePath(resource);
      const url = p.list(meta) + buildQuery(pagination, filters, sorters);
      try {
        const env = await filterApiFetchEnvelope<TData[]>(url);
        const data = env.data ?? [];
        return { data, total: env.pagination?.total ?? data.length };
      } catch (err) {
        throw toHttpError(err);
      }
    },

    async getOne<TData extends BaseRecord = BaseRecord>({
      resource,
      id,
      meta,
    }: Parameters<DataProvider['getOne']>[0]) {
      const p = resolvePath(resource);
      try {
        const data = await filterApiFetch<TData>(p.one(id, meta));
        return { data };
      } catch (err) {
        throw toHttpError(err);
      }
    },

    async getMany<TData extends BaseRecord = BaseRecord>({
      resource,
      ids,
      meta,
    }: Parameters<NonNullable<DataProvider['getMany']>>[0]) {
      const p = resolvePath(resource);
      try {
        const data = await Promise.all(ids.map((id) => filterApiFetch<TData>(p.one(id, meta))));
        return { data };
      } catch (err) {
        throw toHttpError(err);
      }
    },

    async create<TData extends BaseRecord = BaseRecord, TVariables = Record<string, unknown>>({
      resource,
      variables,
      meta,
    }: Parameters<DataProvider['create']>[0] & { variables: TVariables }) {
      const p = resolvePath(resource);
      if (!p.create) throw methodNotAllowed(resource, 'create');
      try {
        const data = await filterApiFetch<TData>(p.create(meta), {
          method: 'POST',
          body: JSON.stringify(variables ?? {}),
        });
        return { data };
      } catch (err) {
        throw toHttpError(err);
      }
    },

    async update<TData extends BaseRecord = BaseRecord, TVariables = Record<string, unknown>>({
      resource,
      id,
      variables,
      meta,
    }: Parameters<DataProvider['update']>[0] & { variables: TVariables }) {
      const p = resolvePath(resource);
      if (!p.update) throw methodNotAllowed(resource, 'update');
      try {
        const data = await filterApiFetch<TData>(p.update(id, meta), {
          method: 'PATCH',
          body: JSON.stringify(variables ?? {}),
        });
        return { data };
      } catch (err) {
        throw toHttpError(err);
      }
    },

    async deleteOne<TData extends BaseRecord = BaseRecord, TVariables = Record<string, unknown>>({
      resource,
      id,
      variables,
      meta,
    }: Parameters<DataProvider['deleteOne']>[0] & { variables?: TVariables }) {
      const p = resolvePath(resource);
      if (!p.delete) throw methodNotAllowed(resource, 'delete');
      try {
        const init: RequestInit = { method: 'DELETE' };
        // Pass optional payload (e.g. { reason } for soft-delete) iff caller supplied it.
        if (variables && Object.keys(variables as Record<string, unknown>).length > 0) {
          init.body = JSON.stringify(variables);
        }
        const data = await filterApiFetch<TData>(p.delete(id, meta), init);
        return { data };
      } catch (err) {
        throw toHttpError(err);
      }
    },
  };
}
